import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useHtmlViewer } from './HtmlViewerContext';
import { saveAnchor } from './hooks/useAnchorResolver';
import type { Annotation } from './HtmlViewerTypes';

const DEFAULT_COLORS = ['#FFD700', '#90EE90', '#87CEEB', '#FFB6C1', '#DDA0DD'];

interface HtmlViewerMemoMenuProps {
  contentRef: React.RefObject<HTMLDivElement>;
  documentId: string;
  colors?: string[];
}

export const HtmlViewerMemoMenu = ({
  contentRef,
  documentId,
  colors = DEFAULT_COLORS,
}: HtmlViewerMemoMenuProps) => {
  const { state, dispatch } = useHtmlViewer();
  const { pendingRange, menuPosition, activeAnnotationId, annotations } = state;

  const [memoText, setMemoText] = useState('');
  const memoInputRef = useRef<HTMLInputElement>(null);

  const activeAnnotation = activeAnnotationId
    ? annotations.find(a => a.id === activeAnnotationId) ?? null
    : null;

  const isVisible = Boolean(
    menuPosition && (pendingRange || activeAnnotation)
  );

  // Sync memo text when active annotation changes
  useEffect(() => {
    setMemoText(activeAnnotation?.memo ?? '');
  }, [activeAnnotation]);

  const close = useCallback(() => {
    dispatch({ type: 'SET_MENU_POSITION', payload: null });
    dispatch({ type: 'SET_PENDING_RANGE', payload: null });
    dispatch({ type: 'SET_ACTIVE_ANNOTATION', payload: null });
    setMemoText('');
  }, [dispatch]);

  const handleColorSelect = useCallback(
    (color: string) => {
      if (!pendingRange || !contentRef.current) return;

      const anchor = saveAnchor(pendingRange, contentRef.current);

      const generateId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      };

      const annotation: Annotation = {
        id: generateId(),
        documentId,
        anchor,
        color,
        createdAt: new Date().toISOString(),
        status: 'active',
      };

      dispatch({ type: 'ADD_ANNOTATION', payload: annotation });

      // Switch to edit mode for this new annotation
      dispatch({ type: 'SET_ACTIVE_ANNOTATION', payload: annotation.id });
      dispatch({ type: 'SET_PENDING_RANGE', payload: null });

      // Focus memo input after state updates
      requestAnimationFrame(() => memoInputRef.current?.focus());
    },
    [pendingRange, contentRef, documentId, dispatch]
  );

  const handleMemoSave = useCallback(() => {
    if (!activeAnnotation) return;

    const updated: Annotation = { ...activeAnnotation, memo: memoText };
    dispatch({ type: 'UPDATE_ANNOTATION', payload: updated });
    close();
  }, [activeAnnotation, memoText, dispatch, close]);

  const handleDelete = useCallback(() => {
    if (!activeAnnotation) return;

    dispatch({ type: 'DELETE_ANNOTATION', payload: activeAnnotation.id });
    close();
  }, [activeAnnotation, dispatch, close]);

  if (!isVisible || !menuPosition) return null;

  return (
    <ClickAwayListener onClickAway={close}>
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          left: menuPosition.x,
          top: menuPosition.y,
          zIndex: 1400,
          minWidth: 220,
          maxWidth: 300,
          p: 1.5,
          borderRadius: 2,
        }}
      >
        {/* New selection: show color picker */}
        {pendingRange && !activeAnnotation && (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              색상 선택
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {colors.map(color => (
                <Tooltip key={color} title={color}>
                  <Box
                    component="button"
                    onClick={() => handleColorSelect(color)}
                    aria-label={`highlight color ${color}`}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: color,
                      border: '2px solid transparent',
                      cursor: 'pointer',
                      '&:hover': {
                        border: '2px solid',
                        borderColor: 'text.primary',
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Stack>
        )}

        {/* Existing annotation: show memo editor */}
        {activeAnnotation && (
          <Stack spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: activeAnnotation.color,
                  flexShrink: 0,
                }}
              />
              <IconButton
                size="small"
                onClick={handleDelete}
                aria-label="delete annotation"
                color="error"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Typography variant="caption" color="text.secondary" noWrap>
              &ldquo;{activeAnnotation.anchor.exact.slice(0, 40)}
              {activeAnnotation.anchor.exact.length > 40 ? '…' : ''}&rdquo;
            </Typography>

            <TextField
              inputRef={memoInputRef}
              value={memoText}
              onChange={e => setMemoText(e.target.value)}
              placeholder="메모를 입력하세요..."
              multiline
              minRows={2}
              maxRows={5}
              size="small"
              fullWidth
              onKeyDown={e => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  handleMemoSave();
                }
                if (e.key === 'Escape') {
                  close();
                }
              }}
            />

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Box
                component="button"
                onClick={close}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                취소
              </Box>
              <Box
                component="button"
                onClick={handleMemoSave}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  border: 'none',
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                }}
              >
                저장
              </Box>
            </Stack>
          </Stack>
        )}
      </Paper>
    </ClickAwayListener>
  );
};
